interface IData {
    dictationFilePaths?: string[],
}

interface IResult {
    playbackPath: string | null,
    shouldPollForMp3: boolean,
}

const WAV_EXTENSIONS = new Set(['wav', 'wave']);
const MP3_EXTENSIONS = new Set(['mp3', 'mpeg', 'mpga']);

/**
 * Reads the file extension from a Firebase Storage path.
 */
const getFileExtension = (filePath: string): string => {
    const fileName = filePath.split('/').pop() || filePath;
    const extension = fileName.split('.').pop();
    return extension ? extension.toLowerCase() : '';
};

/**
 * Replaces the current extension with `.mp3`.
 *
 * Annotation dictation playback now standardizes on MP3. Older rows may still
 * point at legacy FLAC paths, so the browser should request the MP3 companion
 * instead of selecting FLAC directly.
 */
const replaceExtensionWithMp3 = (filePath: string): string => {
    if (!filePath.includes('.')) return `${filePath}.mp3`;
    return filePath.replace(/\.[^/.]+$/, '.mp3');
};

/**
 * Chooses the browser playback path for annotation dictation audio.
 *
 * The fan-out pipeline can briefly leave a source WAV in Firestore before the
 * final MP3 derivative has been written. In that state we should keep polling
 * rather than fetching the WAV. For legacy non-MP3 rows, we derive the MP3
 * companion path instead of selecting FLAC directly.
 */
const selectAnnotationDictationPlaybackPath = ({ dictationFilePaths }: IData): IResult => {
    const normalizedPaths = Array.isArray(dictationFilePaths)
        ? Array.from(new Set(
            dictationFilePaths
                .map((filePath) => filePath?.trim())
                .filter((filePath): filePath is string => !!filePath),
        ))
        : [];

    if (normalizedPaths.length === 0) {
        return {
            playbackPath: null,
            shouldPollForMp3: false,
        };
    }

    const explicitMp3Path = normalizedPaths.find((filePath) => MP3_EXTENSIONS.has(getFileExtension(filePath)));
    if (explicitMp3Path) {
        return {
            playbackPath: explicitMp3Path,
            shouldPollForMp3: false,
        };
    }

    const nonWavPath = normalizedPaths.find((filePath) => !WAV_EXTENSIONS.has(getFileExtension(filePath)));
    if (nonWavPath) {
        return {
            playbackPath: replaceExtensionWithMp3(nonWavPath),
            shouldPollForMp3: false,
        };
    }

    return {
        playbackPath: null,
        shouldPollForMp3: true,
    };
};

export default selectAnnotationDictationPlaybackPath;
